import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FieldError } from "./States";
import { api, formatApiError } from "../../lib/api";

// fields: [{ name, label, required, type: "text" | "select", options: [[value,label]] }]
export default function CrudDialog({ open, onOpenChange, title, fields, initial, endpoint, method, onSaved, testId }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    fields.forEach((f) => { if (f.required && String(form[f.name] ?? "").trim().length < 2) errs[f.name] = `${f.label} requis (2 caractères min.)`; });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" && fields.find((f) => f.name === k)?.nullable ? null : v]));
      const { data } = await api[method](endpoint, body);
      toast.success("Enregistré");
      onSaved(data);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testId}-dialog`} className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" noValidate>
          {fields.map((f) => (
            <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
              <Label>{f.label}</Label>
              {f.type === "multi" ? (
                <div className="mt-1.5 max-h-40 space-y-1 overflow-y-auto rounded-md border p-2" data-testid={`${testId}-${f.name}-multi`}>
                  {!f.options.length && <p className="text-xs text-slate-500">Aucun collaborateur disponible</p>}
                  {f.options.map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={(form[f.name] || []).includes(v)} data-testid={`${testId}-${f.name}-${v}`}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.checked ? [...(form[f.name] || []), v] : (form[f.name] || []).filter((x) => x !== v) })} />{l}
                    </label>
                  ))}
                </div>
              ) : f.type === "select" ? (
                <select className="field-select mt-1.5" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testId}-${f.name}-select`}>
                  {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ) : (
                <Input className="mt-1.5" type={f.inputType || "text"} value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testId}-${f.name}-input`} />
              )}
              <FieldError>{errors[f.name]}</FieldError>
            </div>
          ))}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid={`${testId}-save`}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
